import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../Hooks/redux";
import { logoutRequest, selectUser, selectIsAuthenticated } from "../Redux/AuthRedux";

/**
 * Shared top nav for authenticated app pages (/connected, /success, etc).
 * Left: logo. Right: Google profile pic + email + logout menu.
 */
export default function AppHeader() {
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    dispatch(
      logoutRequest({
        callback: () => navigate("/start", { replace: true }),
      })
    );
  };

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <header className="w-full bg-white border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-900 no-underline">
          <svg width="26" height="26" viewBox="0 0 30 30" fill="none" aria-hidden="true">
            <circle cx="15" cy="15" r="13" stroke="#0DC5C5" strokeOpacity="0.25" strokeWidth="1" />
            <circle cx="15" cy="15" r="8.5" stroke="#0DC5C5" strokeOpacity="0.45" strokeWidth="1" />
            <circle cx="15" cy="15" r="4" stroke="#0DC5C5" strokeOpacity="0.7" strokeWidth="1" />
            <circle cx="15" cy="15" r="1.8" fill="#0DC5C5" />
            <line x1="15" y1="15" x2="23" y2="7" stroke="#0DC5C5" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="23" cy="7" r="1.3" fill="#0DC5C5" opacity="0.7" />
          </svg>
          <span className="text-base font-extrabold tracking-tight">
            Seo<span className="text-teal-600">Sonar</span>
          </span>
        </Link>

        {isAuthenticated && (
          <div className="flex items-center gap-4">
            <Link
              to="/reports"
              className="text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
            >
              My reports
            </Link>
            <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-slate-100 cursor-pointer transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              {user?.image_url ? (
                <img
                  src={user.image_url}
                  alt={user?.name || user?.email || "Profile"}
                  referrerPolicy="no-referrer"
                  className="w-8 h-8 rounded-full border border-slate-200 object-cover"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold flex items-center justify-center">
                  {initial}
                </span>
              )}
              <span className="hidden sm:inline text-sm text-slate-700 font-medium max-w-[180px] truncate">
                {user?.email || ""}
              </span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-slate-500" aria-hidden="true">
                <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20"
              >
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {user?.name || "Signed in"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                >
                  Sign out
                </button>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
